import { z } from 'zod';
import { pageSchemaOf } from './page';
import { Feature, featureFlagManager } from '@/featureflag_manager';

export type Role = 'owner' | 'member';

export const USER_ROLES: { label: string; value: Role }[] = [
  { label: 'Owner', value: 'owner' },
  { label: 'Member', value: 'member' },
];

// Base schema for users (without type field for backward compatibility)
export const workspaceUserSchema = z.object({
  type: z.literal('user').optional(),
  id: z.string(),
  user_name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  email: z.string(),
  profile: z.object({
    given_name: z.string(),
    last_name: z.string(),
  }),
  role: z.enum(['owner', 'member'] as const).nullish(),
});

// Extended schema with type field for mixed user/group responses
let workspaceMemberSchema = workspaceUserSchema;
if (featureFlagManager.isActive(Feature.GROUPS_APPLICATION)) {
  workspaceMemberSchema = workspaceUserSchema.extend({
    type: z.literal('user').optional(),
  });
}

export const workspaceMemberWithTypeSchema = workspaceMemberSchema;

export const workspaceGroupSchema = z.object({
  type: z.literal('group'),
  id: z.string(),
  display_name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  role: z.enum(['owner', 'member'] as const).nullish(),
});

export const workspaceMembersParams = z.object({
  size: z.number().nullish(),
  page: z.number().nullish(),
  email_filter: z.string().nullish(),
});

export const projectMemberParams = workspaceMembersParams.pick({
  size: true,
  page: true,
});

export type WorkspaceMemberParams = z.infer<typeof workspaceMembersParams>;
export type ProjectMemberParams = z.infer<typeof projectMemberParams>;

export type Member = z.infer<typeof workspaceUserSchema>;
export type MemberWithType = z.infer<typeof workspaceMemberWithTypeSchema>;
export type Group = z.infer<typeof workspaceGroupSchema>;
export type MemberOrGroup = MemberWithType | Group;

// Discriminated union for user or group (uses schema with type field)
export const workspaceMemberOrGroupSchema = z.union([
  workspaceMemberWithTypeSchema,
  workspaceGroupSchema,
]);

export const workspaceUserlistSchema = z.array(workspaceUserSchema);
export type WorkspaceUserlist = z.infer<typeof workspaceUserlistSchema>;

// Flexible schema that accepts both formats:
// 1. Users without type field (when feature flag is disabled)
// 2. Users with type field and groups (when feature flag is enabled)
export const workspaceMemberOrGroupListSchema = z.array(
  z.union([
    workspaceUserSchema, // Users without type field (backward compatible)
    workspaceMemberWithTypeSchema, // Users with type field
    workspaceGroupSchema, // Groups
  ])
);
export type WorkspaceMemberOrGroupList = z.infer<typeof workspaceMemberOrGroupListSchema>;

export const workspaceUserListResponseSchema = pageSchemaOf(workspaceUserlistSchema);
export type WorkspaceUserListResponse = z.infer<typeof workspaceUserListResponseSchema>;

// Schema for mixed user/group list (accepts both formats from backend)
export const workspaceMemberOrGroupListResponseSchema = pageSchemaOf(
  workspaceMemberOrGroupListSchema
);

// Type for the normalized response (after frontend normalization)
// All items will have the type field after normalization in getWorkspaceUsersAndGroupsList
export interface WorkspaceMemberOrGroupListResponse {
  size: number;
  page: number;
  total: number;
  num_pages: number;
  items: (MemberWithType | Group)[];
}

export const projectUserSchema = workspaceUserSchema.extend({
  role: z.enum(['owner', 'member'] as const),
});
export type ProjectUser = z.infer<typeof projectUserSchema>;

export const projectUserListResponseSchema = z.array(projectUserSchema);
export type ProjectMemberListResponse = z.infer<typeof projectUserListResponseSchema>;

const upsertSchema = z.object({
  id: z.string(),
  role: z.string(),
});

const removeSchema = z.object({
  id: z.string(),
  role: z.string(),
});

export const patchProjectMemberRequestSchema = z.object({
  upsert: z.array(upsertSchema),
  remove: z.array(removeSchema),
});

export type PatchProjectMemberRequest = z.infer<typeof patchProjectMemberRequestSchema>;
